import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function formatWhatsApp(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

export default function RegistrationForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    idade: '',
    whatsapp: '',
    cidade: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório.';
    const idade = parseInt(formData.idade);
    if (!formData.idade || isNaN(idade) || idade < 3 || idade > 35)
      newErrors.idade = 'Informe uma idade válida entre 3 e 35 anos.';
    const digits = formData.whatsapp.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 11)
      newErrors.whatsapp = 'Informe um WhatsApp válido com DDD. Ex: (66) 99897-7297';
    if (!formData.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('nome', formData.nome);
      formBody.append('idade', formData.idade);
      formBody.append('whatsapp', formData.whatsapp.replace(/\D/g, ''));
      formBody.append('cidade', formData.cidade);
      const response = await fetch('https://readdy.ai/api/form/d6lgvvk6fojmejgaj240', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        navigate('/obrigado');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'whatsapp') {
      setFormData(prev => ({ ...prev, whatsapp: formatWhatsApp(value) }));
    } else if (name === 'idade') {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 2);
      setFormData(prev => ({ ...prev, idade: onlyDigits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <section id="inscricao" className="py-14 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-8 sm:mb-10 lg:mb-14">
          <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">Inscrição gratuita</p>
          <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-3">
            <a href="#inscricao">
              Garanta sua <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">vaga gratuita</span>
            </a>
          </h4>
          <p className="text-sm lg:text-base text-gray-500">Leva menos de 30 segundos para se inscrever.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start justify-center">
          <EventDetails />
          <div className="w-full lg:max-w-xl">
            <form
              id="form-inscricao"
              data-readdy-form={true}
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 border-2 border-gray-100"
            >
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Nome Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    required name="nome" value={formData.nome} onChange={handleChange}
                    className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.nome ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                    placeholder="Nome completo do participante"
                  />
                  {errors.nome && <p className="text-red-500 text-xs mt-1.5">{errors.nome}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Idade <span className="text-red-500">*</span>
                  </label>
                  <input
                    required inputMode="numeric" name="idade" value={formData.idade} onChange={handleChange} maxLength={2}
                    className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.idade ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                    placeholder="Ex: 18"
                  />
                  {errors.idade
                    ? <p className="text-red-500 text-xs mt-1.5">{errors.idade}</p>
                    : <p className="text-gray-400 text-xs mt-1.5">Apenas números entre 3 e 35 anos</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    WhatsApp <span className="text-red-500">*</span>
                  </label>
                  <input
                    required inputMode="numeric" type="tel" name="whatsapp" value={formData.whatsapp} onChange={handleChange}
                    className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.whatsapp ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                    placeholder="(66) 99897-7297"
                  />
                  {errors.whatsapp
                    ? <p className="text-red-500 text-xs mt-1.5">{errors.whatsapp}</p>
                    : <p className="text-gray-400 text-xs mt-1.5">DDD + número. Ex: (66) 99897-7297</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Cidade <span className="text-red-500">*</span>
                  </label>
                  <input
                    required name="cidade" value={formData.cidade} onChange={handleChange}
                    className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.cidade ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                    placeholder="Ex: Tangará da Serra - MT"
                  />
                  {errors.cidade && <p className="text-red-500 text-xs mt-1.5">{errors.cidade}</p>}
                </div>
              </div>

              <button
                type="submit" disabled={isSubmitting}
                className="w-full mt-7 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-4 lg:py-5 rounded-full text-sm lg:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer tracking-wide flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>ENVIANDO...</>
                ) : 'GARANTIR MINHA VAGA GRATUITA'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                  <p className="text-green-700 font-semibold text-sm">✓ Inscrição realizada com sucesso! Em breve entraremos em contato.</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-center">
                  <p className="text-red-700 font-semibold text-sm">Erro ao enviar. Tente novamente.</p>
                </div>
              )}

              <p className="text-center text-xs text-gray-400 mt-5">
                Inscrição 100% gratuita • Seus dados estão seguros
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function EventDetails() {
  const details = [
    { icon: 'ri-calendar-event-fill', label: 'Data', value: '17 de Março de 2026' },
    { icon: 'ri-time-fill', label: 'Horário', value: '19h30 (Terça-feira)' },
    { icon: 'ri-map-pin-2-fill', label: 'Local', value: 'Hotel Íbis — Tangará da Serra - MT' },
    { icon: 'ri-money-dollar-circle-fill', label: 'Investimento', value: '100% Gratuito' },
    { icon: 'ri-group-fill', label: 'Faixa etária', value: '03 a 35 anos' }
  ];

  return (
    <div className="hidden lg:flex flex-col gap-6 w-[380px] xl:w-[420px] flex-shrink-0 pt-2">
      <div className="bg-black rounded-2xl p-8 border border-yellow-500/20">
        <h3 className="text-white font-black text-xl mb-6">Detalhes do Evento</h3>
        <div className="space-y-5">
          {details.map((detail, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="w-9 h-9 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className={`${detail.icon} text-lg sm:text-2xl lg:text-2xl text-black`}></i>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold">{detail.label}</p>
                <p className="text-white text-sm font-semibold mt-0.5">{detail.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <i className="ri-shield-check-fill text-yellow-500 text-xl"></i>
          <p className="text-black font-bold text-sm">Inscrição 100% segura</p>
        </div>
        <p className="text-gray-600 text-xs leading-relaxed">
          Seus dados são utilizados apenas para confirmação da sua vaga. Não compartilhamos com terceiros.
        </p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-fire-fill text-red-500 text-base animate-pulse"></i>
          <p className="text-red-700 font-black text-sm">Apenas 23 vagas restantes!</p>
        </div>
        <p className="text-red-600 text-xs leading-relaxed">
          As vagas são limitadas por horário. Garanta a sua antes que esgotem.
        </p>
      </div>
    </div>
  );
}
